import React, { useState, useMemo, useEffect } from 'react';
import { DynamicComponent } from '../../pages/registry/componentRegistry';
import LayoutEditorControls from '../LayoutEditorControls/LayoutEditorControls';
import './ColumnPanel.css';

function sortByOrder(items, orderKey) {
  return [...items].sort((a, b) => (a[orderKey] ?? 0) - (b[orderKey] ?? 0));
}

const ColumnPanel = ({ title, layout, injections, editMode = false, panelType, pageType, resourceId, highlightedComponentSlugs = [] }) => {
  const sortedSections = useMemo(() => {
    if (!layout?.sections?.length) return [];
    return sortByOrder(layout.sections, 'section_order');
  }, [layout?.sections]);

  const [accordionState, setAccordionState] = useState(() => {
    const initial = {};
    sortedSections.forEach((section, i) => {
      const key = `${section.section_slug || 'section'}-${section.section_order ?? i}-${i}`;
      initial[key] = section.section_default_open ?? true;
    });
    return initial;
  });

  // Expand sections with highlighted components, contract sections without matches when top panel step changes
  const highlightSlugSet = useMemo(() => new Set(highlightedComponentSlugs || []), [highlightedComponentSlugs]);
  useEffect(() => {
    if (!sortedSections.length) return;
    setAccordionState((prev) => {
      const next = { ...prev };
      let changed = false;
      sortedSections.forEach((section, i) => {
        const key = `${section.section_slug || 'section'}-${section.section_order ?? i}-${i}`;
        const hasHighlight = (section.components || []).some((c) => highlightSlugSet.has(c.component_slug));
        const shouldBeOpen = highlightSlugSet.size > 0 ? hasHighlight : (section.section_default_open ?? true);
        if (prev[key] !== shouldBeOpen) {
          next[key] = shouldBeOpen;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [highlightSlugSet, sortedSections]);

  const toggleAccordion = (sectionKey) => {
    setAccordionState((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  if (!layout?.sections?.length) {
    return (
      <div className="column-panel">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2 className="section-title bitter-font">{title}</h2>
        </div>
        <div className="section-subtitle" />
        {editMode && panelType && <LayoutEditorControls panelType={panelType} pageType={pageType} resourceId={resourceId} />}
      </div>
    );
  }

  return (
    <div className="column-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <h2 className="section-title bitter-font">{title}</h2>
      </div>
      <div className="section-subtitle" />
      {editMode && panelType && <LayoutEditorControls panelType={panelType} pageType={pageType} resourceId={resourceId} />}

      {sortedSections.map((section, i) => {
        const sectionKey = `${section.section_slug || 'section'}-${section.section_order ?? i}-${i}`;
        const showTitle = section.section_slug !== 'no-section';
        const isCollapsible = section.section_collapsible === true;
        const isOpen = accordionState[sectionKey] ?? section.section_default_open ?? true;
        const sortedComponents = sortByOrder(section.components || [], 'component_order');

        return (
          <div key={sectionKey} className="noscroll-panel-section">
            {isCollapsible ? (
              <>
                <div
                  onClick={() => toggleAccordion(sectionKey)}
                  className={`noscroll-accordion-header  ${isOpen ? 'expanded' : 'collapsed'}`}
                >
                  {showTitle && (
                    <h2 className="section-title bitter-font">{section.section_title}</h2>
                  )}
                  <span className="noscroll-accordion-icon">{isOpen ? '▼' : '▶'}</span>
                </div>
                {isOpen && (
                  <div className="noscroll-accordion-content">
                    {sortedComponents.map((comp) => {
                      const isHighlighted = highlightSlugSet.has(comp.component_slug);
                      return (
                        <div key={comp.component_slug} className={isHighlighted ? 'column-panel-component-highlight' : undefined}>
                          <DynamicComponent
                            component={comp}
                            injections={injections}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {showTitle && (
                  <div className="noscroll-accordion-header expanded">
                    <h2 className="section-title bitter-font">{section.section_title}</h2>
                  </div>
                )}
                <div className="noscroll-accordion-content">
                  {sortedComponents.map((comp) => {
                    const isHighlighted = highlightSlugSet.has(comp.component_slug);
                    return (
                      <div key={comp.component_slug} className={isHighlighted ? 'column-panel-component-highlight' : undefined}>
                        <DynamicComponent
                          component={comp}
                          injections={injections}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ColumnPanel;
